"use client";

// Fires the PostHog `sign_up_complete` funnel event at the first CONSENTED
// signed-in browser paint after signup. The Clerk webhook cannot corroborate
// the browser's optional Analytics choice, so the server-side user.created
// capture was retired with granular consent (v2) — but the onboarding flow
// alone cannot replace it: signups that ride an explicit redirect_url
// (claim-callback, checkout, invitations) never reach onboarding. Mounted
// inside <PostHogProvider> in every ClerkProvider segment — (app), (auth),
// (funnel), (onboarding) — so each product signup flow shares one capture
// path; useUser needs the Clerk context, so segments without it cannot mount
// this. A hand-crafted /signup?redirect_url=<marketing path> therefore
// captures on the user's first Clerk-segment visit inside the freshness
// window rather than on the marketing page itself — no product flow emits
// such a redirect. The root's isolated Clerk boundary deliberately carries
// only the privacy-critical Account consent bridge across public layouts; it
// does not turn those layouts into signup-completion surfaces. Tracked with
// the residual consent items on issue #1880.
//
// Once-semantics: a per-user localStorage marker plus a freshness window.
// Without the window, every EXISTING user with an unmarked browser would emit
// a bogus completion on their next visit; within it, a second device inside
// the same day can double-fire — acceptable for person-level funnels.

import { useEffect } from "react";
import { useUser } from "@clerk/nextjs";
import posthog from "posthog-js";
import { readAnalyticsConsent } from "@/components/analytics/browser-attribution";
import { subscribeToBrowserConsentCategory } from "@/components/analytics/browser-consent-lifecycle";
import {
  POSTHOG_BROWSER_INITIALIZED_EVENT,
  POSTHOG_IDENTITY_READY_EVENT,
} from "@/lib/analytics/posthog-browser-lifecycle";
import {
  ANALYTICS_LOCATION_CHANGE_EVENT,
  isSuppressedUrlString,
} from "@/lib/analytics/suppressed-paths";

const SIGN_UP_COMPLETE_MARKER_PREFIX = "tsb_sign_up_complete_captured:";
const RECENT_SIGNUP_WINDOW_MS = 24 * 60 * 60 * 1000;

// Per-user once-state: a user-keyed durable marker (so a second signup on a
// shared browser cannot erase the first user's dedupe state), backed by an
// in-page memory set so a storage-less browser still cannot re-capture on
// every later consent-lifecycle notification.
const capturedThisPage = new Set<string>();

function hasCaptured(userId: string): boolean {
  if (capturedThisPage.has(userId)) return true;
  try {
    return (
      window.localStorage.getItem(SIGN_UP_COMPLETE_MARKER_PREFIX + userId) ===
      "1"
    );
  } catch {
    // Storage-less browsers dedupe via the in-memory set for this page's
    // lifetime; a duplicate across page loads stays possible but rare, and
    // losing the event entirely would be worse.
    return false;
  }
}

function markCaptured(userId: string): void {
  capturedThisPage.add(userId);
  try {
    window.localStorage.setItem(SIGN_UP_COMPLETE_MARKER_PREFIX + userId, "1");
  } catch {
    // Best-effort; see hasCaptured.
  }
}

export function SignupCompleteTracker() {
  const { user, isLoaded } = useUser();
  const userId = user?.id;
  const createdAtMs = user?.createdAt?.getTime() ?? null;

  useEffect(() => {
    if (!isLoaded || !userId) return;
    if (
      createdAtMs === null ||
      Date.now() - createdAtMs > RECENT_SIGNUP_WINDOW_MS
    ) {
      return;
    }

    function captureIfReady() {
      if (
        !userId ||
        !posthog.__loaded ||
        readAnalyticsConsent() !== "accepted" ||
        posthog.get_distinct_id() !== userId ||
        hasCaptured(userId)
      ) {
        return;
      }
      // On a suppressed (token-bearing) URL — e.g. a signup whose
      // redirect_url query carries a token path — the provider's before_send
      // drops the event, and an opted-out singleton captures nothing at all.
      // Marking then would permanently lose the signup: skip WITHOUT marking
      // so the next safe landing (or SPA navigation) retries.
      if (posthog.has_opted_out_capturing?.()) return;
      if (
        isSuppressedUrlString(
          `${window.location.pathname}${window.location.search}`,
        )
      ) {
        return;
      }
      posthog.capture("sign_up_complete", { source: "post_signup_landing" });
      markCaptured(userId);
    }

    function onConsentChange() {
      // Let the provider apply the authoritative cookie to the SDK first.
      queueMicrotask(captureIfReady);
    }

    captureIfReady();
    window.addEventListener(POSTHOG_BROWSER_INITIALIZED_EVENT, captureIfReady);
    window.addEventListener(POSTHOG_IDENTITY_READY_EVENT, captureIfReady);
    // SPA navigation away from a suppressed URL is the retry moment the
    // skip-without-marking above depends on.
    window.addEventListener(ANALYTICS_LOCATION_CHANGE_EVENT, captureIfReady);
    const unsubscribeConsent = subscribeToBrowserConsentCategory(
      "analytics",
      onConsentChange,
    );
    return () => {
      window.removeEventListener(
        POSTHOG_BROWSER_INITIALIZED_EVENT,
        captureIfReady,
      );
      window.removeEventListener(POSTHOG_IDENTITY_READY_EVENT, captureIfReady);
      window.removeEventListener(
        ANALYTICS_LOCATION_CHANGE_EVENT,
        captureIfReady,
      );
      unsubscribeConsent();
    };
  }, [isLoaded, userId, createdAtMs]);

  return null;
}
